import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { db } from '../../../../firebase-config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Button, Card, Divider, message as antMessage, Tabs } from 'antd';

const { TabPane } = Tabs;

// Quill editor configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    ['link'],
    ['clean']
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'color', 'background',
  'align',
  'link'
];

const MiscellaneousEditor = () => {
  const [affiliatedRituals, setAffiliatedRituals] = useState('');
  const [copyright, setCopyright] = useState('');
  const [termsOfUse, setTermsOfUse] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);



  useEffect(() => {
    const fetchMiscellaneous = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'siteContent', 'miscellaneous');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log('Miscellaneous Editor - Loaded data:', data);
          setAffiliatedRituals(data.affiliatedRituals || '');
          setCopyright(data.copyright || '');
          setTermsOfUse(data.termsOfUse || '');
        } else {
          console.log('Miscellaneous Editor - No data found');
          setAffiliatedRituals('');
          setCopyright('');
          setTermsOfUse('');
        }
      } catch (error) {
        console.error('Miscellaneous Editor - Error loading:', error);
        antMessage.error('Failed to load miscellaneous content.');
      }
      setLoading(false);
    };
    fetchMiscellaneous();
  }, []);

  const handleSave = async (type) => {
    setSaving(true);
    try {
      const docRef = doc(db, 'siteContent', 'miscellaneous');
      const currentData = (await getDoc(docRef)).data() || {};
      
      const fieldValue = type === 'affiliatedRituals' ? affiliatedRituals : 
                        type === 'copyright' ? copyright : termsOfUse;
      
      const updatedData = {
        ...currentData,
        [type]: fieldValue
      };
      
      await setDoc(docRef, updatedData);
      
      // No verification log in production
      antMessage.success(`${type === 'affiliatedRituals' ? 'Affiliated Rituals' : 
                         type === 'copyright' ? 'Copyright' : 'Terms of Use'} updated successfully!`);
    } catch (error) {
      antMessage.error(`Failed to save ${type === 'affiliatedRituals' ? 'Affiliated Rituals' : 
                       type === 'copyright' ? 'Copyright' : 'Terms of Use'}.`);
    }
    setSaving(false);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const saveData = {
        affiliatedRituals,
        copyright,
        termsOfUse
      };
      console.log('Saving all miscellaneous content:', saveData); // Debug log
      await setDoc(doc(db, 'siteContent', 'miscellaneous'), saveData);
      antMessage.success('All miscellaneous content updated successfully!');
    } catch (error) {
      console.error('Error saving all miscellaneous content:', error); // Debug log
      antMessage.error('Failed to save miscellaneous content.');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 800, margin: '0 auto', padding: 24 }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
      <style>
        {`
          .ql-editor {
            min-height: 200px !important;
            font-size: 14px !important;
            line-height: 1.6 !important;
          }
          .ql-toolbar {
            border-top: 1px solid #ccc !important;
            border-left: 1px solid #ccc !important;
            border-right: 1px solid #ccc !important;
            border-top-left-radius: 6px !important;
            border-top-right-radius: 6px !important;
          }
          .ql-container {
            border-bottom: 1px solid #ccc !important;
            border-left: 1px solid #ccc !important;
            border-right: 1px solid #ccc !important;
            border-bottom-left-radius: 6px !important;
            border-bottom-right-radius: 6px !important;
          }
        `}
      </style>
      <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>
        Edit Miscellaneous Content
      </h1>
      
      <Tabs defaultActiveKey="affiliatedRituals" type="card">
        <TabPane tab="Affiliated Rituals" key="affiliatedRituals">
          <Card title="Affiliated Rituals Content">
            <p style={{ marginBottom: '16px', color: '#666' }}>
              Edit the Affiliated Rituals content that will be displayed in the footer.
            </p>
            <ReactQuill 
              value={affiliatedRituals} 
              onChange={setAffiliatedRituals}
              placeholder="Enter Affiliated Rituals content..."
              modules={quillModules}
              formats={quillFormats}
              style={{ marginBottom: '16px' }}
            />
            <Button 
              type="primary" 
              onClick={() => handleSave('affiliatedRituals')} 
              loading={saving}
              style={{ marginTop: '16px' }}
            >
              {saving ? 'Saving...' : 'Save Affiliated Rituals'}
            </Button>
          </Card>
        </TabPane>

        <TabPane tab="Copyright" key="copyright">
          <Card title="Copyright Content">
            <p style={{ marginBottom: '16px', color: '#666' }}>
              Edit the copyright information that will be displayed in the footer.
            </p>
            <ReactQuill 
              value={copyright} 
              onChange={setCopyright}
              placeholder="Enter copyright content..."
              modules={quillModules}
              formats={quillFormats}
              style={{ marginBottom: '16px' }}
            />
            <Button 
              type="primary" 
              onClick={() => handleSave('copyright')} 
              loading={saving}
              style={{ marginTop: '16px' }}
            >
              {saving ? 'Saving...' : 'Save Copyright'}
            </Button>
          </Card>
        </TabPane>

        <TabPane tab="Terms of Use | Privacy" key="termsOfUse">
          <Card title="Terms of Use | Privacy Content">
            <p style={{ marginBottom: '16px', color: '#666' }}>
              Edit the Terms of Use and Privacy content that will be displayed in the footer.
            </p>
            <ReactQuill 
              value={termsOfUse} 
              onChange={setTermsOfUse}
              placeholder="Enter Terms of Use | Privacy content..."
              modules={quillModules}
              formats={quillFormats}
              style={{ marginBottom: '16px' }}
            />
            <Button 
              type="primary" 
              onClick={() => handleSave('termsOfUse')} 
              loading={saving}
              style={{ marginTop: '16px' }}
            >
              {saving ? 'Saving...' : 'Save Terms of Use | Privacy'}
            </Button>
          </Card>
        </TabPane>
      </Tabs>

      <Divider />

      <Card title="Save All Content">
        <p style={{ marginBottom: '16px', color: '#666' }}>
          Save all miscellaneous content at once.
        </p>
        <Button 
          type="primary" 
          size="large"
          onClick={handleSaveAll} 
          loading={saving}
        >
          {saving ? 'Saving All...' : 'Save All Miscellaneous Content'}
        </Button>
      </Card>

      <Divider />

    </div>
  );
};

export default MiscellaneousEditor; 